import React from "react";

interface TooltipPayloadItem {
	name: string;
	value: string | number;
	color?: string;
}

export const formatDateDDMMYYYY = (dateStr: string): string => {
	if (!dateStr) return dateStr;
	const parts = dateStr.split("-");
	if (parts.length !== 3) return dateStr;
	return `${parts[2]}-${parts[1]}-${parts[0]}`;
};

export const titleCase = (str: any) => {
	if (str == null) return "";
	str = str.toString();
	str = str.replaceAll("_", " ");
	return str.toLowerCase().replace(/\b(\w)/g, (s: any) => s.toUpperCase());
};

const tooltipContainerStyle: React.CSSProperties = {
	backgroundColor: "#ffffff",
	border: "1px solid #e0e0e0",
	borderRadius: "8px",
	padding: "12px 16px",
	boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
	maxHeight: "300px",
	overflowY: "auto",
	minWidth: "200px",
	maxWidth: "350px",
	zIndex: 9999,
};

const tooltipHeaderStyle: React.CSSProperties = {
	fontSize: "13px",
	fontWeight: 600,
	color: "#1a1a1a",
	marginBottom: "6px",
	borderBottom: "1px solid #eee",
	paddingBottom: "6px",
};

const tooltipAvgStyle: React.CSSProperties = {
	fontSize: "12px",
	fontWeight: 600,
	color: "#555",
	marginBottom: "6px",
};

const tooltipRowStyle: React.CSSProperties = {
	display: "flex",
	alignItems: "center",
	gap: "6px",
	padding: "2px 0",
	fontSize: "12px",
	color: "#333",
};

const colorDotStyle = (color: string): React.CSSProperties => ({
	width: "8px",
	height: "8px",
	borderRadius: "50%",
	backgroundColor: color,
	flexShrink: 0,
});

type UtilizationTooltipProject = {
	project_name?: string | null;
	project_key?: string | null;
	hours_logged?: number | null;
};

export const CustomTooltip = ({ active, payload, label, hideWeeklyAverage = false }: any) => {
	if (!active || !label || !payload?.length) return null;

	const uniqueNames = new Set();
	const row = payload[0]?.payload as
		| { __utilizationTooltip?: Record<string, UtilizationTooltipProject[]> }
		| undefined;
	const byEmployee = row?.__utilizationTooltip;

	// Sort payload by value (desc) then by name (alphabetically)
	const sortedPayload = [...payload].sort((a, b) => {
		const valueA = parseFloat(a?.value?.toString()) || 0;
		const valueB = parseFloat(b?.value?.toString()) || 0;
		
		// First sort by value (descending)
		if (valueB !== valueA) {
			return valueB - valueA;
		}
		
		// Then sort alphabetically by name
		const nameA = String(a?.name || "").toLowerCase();
		const nameB = String(b?.name || "").toLowerCase();
		return nameA.localeCompare(nameB);
	});

	const values: number[] = sortedPayload.map(
		(ele: TooltipPayloadItem) => parseFloat(ele?.value?.toString()) || 0
	);
	const average: number =
		values.length > 0
			? values.reduce((a: number, b: number) => a + b, 0) / values.length
			: 0;
	let totalHrs = 0;
	let hasAnyHours = false;
	for (const ele of sortedPayload) {
		const seriesKey = String(ele?.dataKey ?? ele?.name ?? "");
		const projects: UtilizationTooltipProject[] =
			(seriesKey && byEmployee?.[seriesKey]) || [];
		for (const p of projects) {
			const h = p.hours_logged;
			if (h != null && !Number.isNaN(Number(h))) {
				totalHrs += Number(h);
				hasAnyHours = true;
			}
		}
	}

	return (
		<div style={tooltipContainerStyle}>
			<div style={tooltipHeaderStyle}>{formatDateDDMMYYYY(label)}</div>
			{!hideWeeklyAverage && (
				<div style={tooltipAvgStyle}>
					{hasAnyHours
						? `Total hours: ${totalHrs.toFixed(2)} h`
						: `Weekly average: ${average.toFixed(2)}`}
				</div>
			)}
			{sortedPayload.map((ele: any, index: number) => {
				if (uniqueNames.has(ele?.name)) return null;
				uniqueNames.add(ele?.name);
				const val = parseFloat(ele?.value);
				const seriesKey = String(ele?.dataKey ?? ele?.name ?? "");
				const projects: UtilizationTooltipProject[] =
					(seriesKey && byEmployee?.[seriesKey]) || [];
				return (
					<div key={index} style={{ marginBottom: projects.length ? 8 : 0 }}>
						<div style={tooltipRowStyle}>
							<span style={colorDotStyle(ele?.color || "#999")} />
							<span style={{ flex: 1 }}>{titleCase(ele?.name)}</span>
							<span style={{ fontWeight: 600 }}>
								{isNaN(val) ? "—" : val.toFixed(2)}
							</span>
						</div>
						{projects.length > 0 && (
							<div
								style={{
									...tooltipRowStyle,
									flexDirection: "column",
									alignItems: "stretch",
									paddingLeft: 14,
									fontSize: "11px",
									color: "#555",
									maxHeight: 120,
									overflowY: "auto",
								}}
							>
								{projects.map((p, i) => {
									const labelProj =
										(p.project_key || p.project_name) ?? "—";
									const h = p.hours_logged;
									const hrs =
										h == null || Number.isNaN(Number(h))
											? "—"
											: `${Number(h).toFixed(2)}h`;
									return (
										<div key={i}>
											{labelProj}: {hrs}
										</div>
									);
								})}
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
};

type JiraBarProjectRow = {
	project_name: string;
	project_key?: string;
	count: number;
	bug_keys?: string[];
};

type JiraBarSeriesMeta = {
	by_project?: JiraBarProjectRow[];
	breached_issues?: { key?: string | null; issue_type?: string | null }[];
	nc_issues?: { issue_key?: string | null; title?: string | null }[];
};

type BreachedIssueRow = { key?: string | null; issue_type?: string | null };
function dedupeBreachedIssuesByKey(items: BreachedIssueRow[]): BreachedIssueRow[] {
	const seen = new Set<string>();
	const out: BreachedIssueRow[] = [];
	for (const b of items) {
		const k = b.key;
		if (k != null && k !== "") {
			if (seen.has(k)) continue;
			seen.add(k);
		}
		out.push(b);
	}
	return out;
}
export type JiraEmployeeBarTooltipProps = {
	active?: boolean;
	payload?: any[];
	label?: any;
	scopedSeriesName?: string;
};

const detailListStyle: React.CSSProperties = {
	...tooltipRowStyle,
	flexDirection: "column",
	alignItems: "stretch",
	paddingLeft: 14,
	fontSize: "11px",
	color: "#555",
	maxHeight: 120,
	overflowY: "auto",
};

export const JiraEmployeeBarTooltip = ({
	active,
	payload,
	label,
	scopedSeriesName,
}: JiraEmployeeBarTooltipProps) => {
	if (!active || !payload?.length) return null;
	const row = payload[0]?.payload as Record<string, unknown> | undefined;
	const meta = row?.__jiraTooltip as Record<string, JiraBarSeriesMeta> | undefined;

	const rows = scopedSeriesName
		? payload.filter(
				(ele: any) =>
					String(ele?.dataKey ?? ele?.name ?? "") === scopedSeriesName
		  )
		: payload;

	// Sort payload by value (desc) then by name (alphabetically)
	const sortedPayload = [...payload].sort((a, b) => {
		const valueA = parseFloat(a?.value?.toString()) || 0;
		const valueB = parseFloat(b?.value?.toString()) || 0;
		
		// First sort by value (descending)
		if (valueB !== valueA) {
			return valueB - valueA;
		}
		
		// Then sort alphabetically by name
		const nameA = String(a?.name || "").toLowerCase();
		const nameB = String(b?.name || "").toLowerCase();
		return nameA.localeCompare(nameB);
	});

	// Sort rows as well if we have scopedSeriesName
	const sortedRows = scopedSeriesName
		? [...rows].sort((a, b) => {
				const valueA = parseFloat(a?.value?.toString()) || 0;
				const valueB = parseFloat(b?.value?.toString()) || 0;
				
				if (valueB !== valueA) {
					return valueB - valueA;
				}
				
				const nameA = String(a?.name || "").toLowerCase();
				const nameB = String(b?.name || "").toLowerCase();
				return nameA.localeCompare(nameB);
		  })
		: sortedPayload;

	const values: number[] = sortedRows.map(
		(ele: TooltipPayloadItem) => parseFloat(ele?.value?.toString()) || 0
	);
	const totalFocused =
		values.length > 0 ? values.reduce((a: number, b: number) => a + b, 0) : 0;
	const averageAll =
		sortedPayload.length > 0
			? sortedPayload
					.map((ele: TooltipPayloadItem) => parseFloat(ele?.value?.toString()) || 0)
					.reduce((a: number, b: number) => a + b, 0) / sortedPayload.length
			: 0;

	return (
		<div style={tooltipContainerStyle}>
			<div style={tooltipHeaderStyle}>{formatDateDDMMYYYY(label)}</div>
			<div style={tooltipAvgStyle}>
				{scopedSeriesName
					? `Total (this employee): ${totalFocused.toFixed(0)}`
					: `Weekly average: ${averageAll.toFixed(2)}`}
			</div>
			{(scopedSeriesName ? sortedRows : sortedPayload).map((ele: any, index: number) => {
				const key = String(ele?.dataKey ?? ele?.name ?? "");
				if (!key || key === "week") return null;
				const val = parseFloat(ele?.value);
				const m = meta?.[key];
				const projects = m?.by_project ?? [];
				const breached = dedupeBreachedIssuesByKey(m?.breached_issues ?? []);
				const nc = m?.nc_issues ?? [];
				return (
					<div key={index} style={{ marginBottom: 8 }}>
						<div style={tooltipRowStyle}>
							<span style={colorDotStyle(ele?.color || "#999")} />
							<span style={{ flex: 1 }}>{titleCase(ele?.name)}</span>
							<span style={{ fontWeight: 600 }}>
								{isNaN(val) ? "—" : val.toFixed(0)}
							</span>
						</div>
						{projects.length > 0 && (
							<div style={detailListStyle}>
								{projects.map((p, i) => (
									<div key={i} style={{ marginBottom: 4 }}>
										<div>
											{(p.project_key || p.project_name) ?? "—"}: {p.count}
										</div>
										{p.bug_keys && p.bug_keys.length > 0 && (
											<div style={{ paddingLeft: 8, wordBreak: "break-all" }}>
												{p.bug_keys.join(", ")}
											</div>
										)}
									</div>
								))}
							</div>
						)}
						{breached.length > 0 && (
							<div style={detailListStyle}>
								{breached.map((b, i) => (
									<div key={i}>
										{(b.key ?? "—") as string}
										{b.issue_type != null && b.issue_type !== ""
											? ` · ${b.issue_type}`
											: ""}
									</div>
								))}
							</div>
						)}
						{nc.length > 0 && (
							<div style={detailListStyle}>
								{nc.map((it, i) => (
									<div key={i}>
										{(it.issue_key ?? "—") as string}
										{it.title != null && it.title !== ""
											? ` · ${it.title}`
											: ""}
									</div>
								))}
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
};

export const RenderLegend = (props: any) => {
	const { payload } = props;
	return (
		<div className="d-flex justify-content-end">
			{payload.map((entry: any, index: number) => (
				<span className="mx-2" key={`item-${index}`}>
					{entry.value}
				</span>
			))}
		</div>
	);
};
